import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ConversationListResponse } from '@sdr-agend/admin-schemas'

export interface CarteiraCustomerListProps {
  sdrId: string
}

/**
 * Story 12.10 (Epic E12) — lista SOMENTE LEITURA dos leads da carteira de
 * uma SDR, pra tela `/admin/carteiras` (manager/admin acompanhando, sem
 * reatribuir — isso já existe em `CarteiraDialog.tsx`, admin-only,
 * Story 12.6).
 *
 * Reaproveita `GET /admin/conversations?sdrId=X` (Story 12.4) SEM
 * `dateFrom`/`dateTo` — lista a carteira inteira, não um recorte de
 * período, mesmo padrão que `CarteiraDialog` já usa.
 */
export function CarteiraCustomerList({ sdrId }: CarteiraCustomerListProps): React.ReactElement {
  const { data, isLoading, isError } = useQuery<ConversationListResponse>({
    queryKey: ['admin', 'conversations', 'sdr-carteira', sdrId],
    queryFn: async () => api<ConversationListResponse>(`/admin/conversations?sdrId=${sdrId}&perPage=100`),
  })

  const items = data?.items ?? []

  return (
    <div className="max-h-[50vh] overflow-y-auto rounded-md border bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Lead</TableHead>
            <TableHead>Telefone</TableHead>
            <TableHead>Campanha</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Criado em</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                Carregando…
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-destructive">
                Erro ao carregar a carteira.
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                Nenhum lead atribuído a esta SDR ainda.
              </TableCell>
            </TableRow>
          )}
          {items.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">{c.customerName ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs">{c.customerPhone}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {c.campaignName ?? '—'}
              </TableCell>
              <TableCell className="text-sm">{c.status}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(c.createdAt).toLocaleString('pt-BR')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default CarteiraCustomerList
